import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
  AttachMoney,
  LocalShipping,
  Today,
  Description,
  Delete,
  AccessTime,
  Save,
} from '@mui/icons-material';
import {
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from '@mui/material';

import { SendOfferRequestBody, useOffer } from '../context/OfferContext';
import { Inquiry } from '../models/Inquiry';
import { DeliveryMethod } from '../models/Order';

enum DeliveryTime {
  '1-3 dni',
  '3-5 dni',
  '5-7 dni',
  '7-14 dni',
  '14+ dni',
}

type Props = {
  inquiry: Inquiry;
  offerDetails?: string;
  validUntil?: string;
  deliveryPrice?: number;
  deliveryMethod?: DeliveryMethod;
  productPrices?: {
    id: number;
    price: number;
    quantity: number;
    deliveryTime: DeliveryTime;
    removeProductFromOffer?: boolean;
  }[];
};

type FormData = {
  offerDetails?: string;
  validUntil: string;
  deliveryPrice: number;
  deliveryMethod: DeliveryMethod;
  productPrices: {
    id: number;
    price: number;
    quantity: number;
    deliveryTime: DeliveryTime;
    removeProductFromOffer?: boolean;
  }[];
};

const deliveryTimeOptions = [
  { label: '1-3 dni', value: DeliveryTime['1-3 dni'] },
  { label: '3-5 dni', value: DeliveryTime['3-5 dni'] },
  { label: '5-7 dni', value: DeliveryTime['5-7 dni'] },
  { label: '7-14 dni', value: DeliveryTime['7-14 dni'] },
  { label: '14+ dni', value: DeliveryTime['14+ dni'] },
];

function OfferForm({ inquiry }: Props) {
  // Ustaw domyślną datę ważności na 14 dni od dziś
  const defaultValidUntil = (() => {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date.toISOString().split('T')[0];
  })();

  // Stan do przechowywania łącznej wartości oferty
  const [totalValue, setTotalValue] = useState<number>(0);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      offerDetails: '',
      validUntil: defaultValidUntil,
      deliveryPrice: 0,
      deliveryMethod: DeliveryMethod.KURIER_INPOST,
      productPrices: [],
    },
  });

  // Obserwuj zmiany w cenach produktów i cenie dostawy
  const watchProductPrices = watch('productPrices');
  const watchDeliveryPrice = watch('deliveryPrice');

  // Oblicz całkowitą wartość oferty
  useEffect(() => {
    if (watchProductPrices) {
      try {
        const productTotal = watchProductPrices.reduce((total, product) => {
          if (!product || product.removeProductFromOffer) return total;

          // Konwersja na liczby i weryfikacja poprawności
          const price = parseFloat(String(product.price)) || 0;
          const quantity = parseInt(String(product.quantity)) || 0;

          // Dodaj wartość tylko jeśli obie wartości są dodatnie
          if (price > 0 && quantity > 0) {
            return total + price * quantity;
          }
          return total;
        }, 0);

        // Konwersja kosztu dostawy na liczbę
        const deliveryTotal = parseFloat(String(watchDeliveryPrice)) || 0;

        // Ustal całkowitą wartość z dokładnością do 2 miejsc po przecinku
        const finalTotal = Math.round((productTotal + deliveryTotal) * 100) / 100;
        setTotalValue(finalTotal);
      } catch (error) {
        console.error('Błąd podczas obliczania wartości oferty:', error);
        setTotalValue(0);
      }
    } else {
      setTotalValue(0);
    }
  }, [watchProductPrices, watchDeliveryPrice]);

  // Zainicjuj wartości produktów
  useEffect(() => {
    if (inquiry.products && inquiry?.products?.length > 0) {
      const initializedPrices = inquiry.products.map((product) => ({
        id: product.productId,
        price: product.product?.price || 0,
        quantity: product.quantity,
        deliveryTime: DeliveryTime['1-3 dni'],
        removeProductFromOffer: false,
      }));
      setValue('productPrices', initializedPrices);
    }
  }, [inquiry, setValue]);

  const { sendOffer } = useOffer();
  const navigate = useNavigate();

  const onSubmit = async (data: FormData) => {
    // Sprawdź, czy wszystkie produkty są usunięte
    const allProductsRemoved = data.productPrices.every(
      (product) => product.removeProductFromOffer,
    );

    if (allProductsRemoved) {
      toast.error(
        'Nie można utworzyć oferty bez produktów. Dodaj przynajmniej jeden produkt.',
      );
      return;
    }

    const requestBody: SendOfferRequestBody = {
      inquiryId: inquiry.id,
      offerDetails: data.offerDetails || '',
      validUntil: data.validUntil,
      deliveryPrice: data.deliveryPrice,
      deliveryMethod: data.deliveryMethod,
      products: data.productPrices.map((product) => ({
        id: product.id,
        price: product.price,
        quantity: product.quantity,
        removeProductFromOffer: product.removeProductFromOffer,
      })),
    };

    const toastId = toast.loading('Wysyłanie oferty...');
    try {
      await sendOffer(requestBody);
      toast.update(toastId, {
        render: 'Oferta została wysłana!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      navigate('/admin/oferty');
    } catch (error: any) {
      toast.update(toastId, {
        render: error.message || 'Nie udało się wysłać oferty',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      console.error('Failed to send offer', error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-lg shadow-sm p-6"
    >
      <h2 className="text-xl font-bold mb-6 text-gray-800">Szczegóły oferty</h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Sekcja podstawowych informacji */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <div className="flex items-center mb-2">
              <Description className="text-primary mr-2" />
              <label className="text-sm font-medium text-gray-700">
                Dodatkowe informacje o ofercie
              </label>
            </div>
            <Controller
              name="offerDetails"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  placeholder="Np. specjalne warunki dostawy, promocyjne ceny, etc."
                  multiline
                  rows={3}
                  fullWidth
                  error={!!errors.offerDetails}
                  helperText={errors.offerDetails?.message || ''}
                  className="bg-gray-50"
                />
              )}
            />
          </div>

          <div>
            <div className="flex items-center mb-2">
              <Today className="text-primary mr-2" />
              <label className="text-sm font-medium text-gray-700">
                Data ważności oferty
              </label>
            </div>
            <Controller
              name="validUntil"
              control={control}
              rules={{ required: 'Data ważności jest wymagana' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.validUntil}
                  helperText={errors.validUntil?.message || ''}
                  className="bg-gray-50"
                />
              )}
            />
          </div>

          <div>
            <div className="flex items-center mb-2">
              <AttachMoney className="text-primary mr-2" />
              <label className="text-sm font-medium text-gray-700">
                Koszt dostawy (zł)
              </label>
            </div>
            <Controller
              name="deliveryPrice"
              control={control}
              rules={{
                required: 'Koszt dostawy jest wymagany',
                min: { value: 0, message: 'Koszt dostawy nie może być ujemny' },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  variant="outlined"
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  error={!!errors.deliveryPrice}
                  helperText={errors.deliveryPrice?.message || ''}
                  className="bg-gray-50"
                />
              )}
            />
          </div>

          <div>
            <div className="flex items-center mb-2">
              <LocalShipping className="text-primary mr-2" />
              <label className="text-sm font-medium text-gray-700">
                Metoda dostawy
              </label>
            </div>
            <Controller
              name="deliveryMethod"
              control={control}
              rules={{ required: 'Metoda dostawy jest wymagana' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  variant="outlined"
                  fullWidth
                  error={!!errors.deliveryMethod}
                  helperText={errors.deliveryMethod?.message || ''}
                  className="bg-gray-50"
                >
                  <MenuItem value={DeliveryMethod.KURIER_INPOST}>
                    Kurier InPost
                  </MenuItem>
                  <MenuItem value={DeliveryMethod.PACZKOMAT}>
                    Paczkomat InPost
                  </MenuItem>
                </TextField>
              )}
            />
          </div>
        </div>

        {/* Sekcja produktów */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Produkty w ofercie
            </h3>
            <div className="bg-blue-50 px-3 py-2 rounded-md">
              <p className="text-sm text-gray-600">
                Wartość oferty:{' '}
                <span className="font-bold text-primary">
                  {(typeof totalValue === 'number' ? totalValue : 0).toFixed(2)}{' '}
                  zł
                </span>
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {inquiry.products?.map((product, index) => (
              <motion.div
                key={product.productId}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`border ${
                  watchProductPrices?.[index]?.removeProductFromOffer
                    ? 'border-red-200 bg-red-50'
                    : 'border-gray-200 bg-white'
                } rounded-lg p-4`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                  <div className="h-16 w-16 bg-white border border-gray-100 rounded-md overflow-hidden flex-shrink-0">
                    {product.product?.mobilePrimaryImage ? (
                      <img
                        src={product.product.mobilePrimaryImage}
                        alt={product.product?.name}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="h-full w-full bg-gray-100 flex items-center justify-center text-gray-400">
                        ?
                      </div>
                    )}
                  </div>

                  <div className="flex-grow">
                    <h4 className="font-medium text-gray-900">
                      {product.product?.name || 'Nieznany produkt'}
                    </h4>
                    <p className="text-sm text-gray-500">
                      SKU: {product.product?.serialNumber || 'N/A'} | Ilość
                      zamówiona: {product.quantity} szt.
                    </p>
                    {watchProductPrices?.[index]?.removeProductFromOffer && (
                      <p className="text-sm font-medium text-red-500 mt-1">
                        Ten produkt zostanie usunięty z oferty
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center mb-2">
                      <AttachMoney
                        className="text-primary mr-1"
                        fontSize="small"
                      />
                      <label className="text-xs font-medium text-gray-700">
                        Cena za sztukę (zł)
                      </label>
                    </div>
                    <Controller
                      name={`productPrices.${index}.price`}
                      control={control}
                      rules={{
                        required: 'Cena jest wymagana',
                        min: { value: 0, message: 'Cena nie może być ujemna' },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          inputProps={{ min: 0, step: 0.01 }}
                          error={!!errors.productPrices?.[index]?.price}
                          helperText={
                            errors.productPrices?.[index]?.price?.message || ''
                          }
                          disabled={
                            watchProductPrices?.[index]?.removeProductFromOffer
                          }
                          className={
                            watchProductPrices?.[index]?.removeProductFromOffer
                              ? 'opacity-50'
                              : ''
                          }
                        />
                      )}
                    />
                    {watchProductPrices?.[index]?.price &&
                      watchProductPrices?.[index]?.quantity &&
                      !watchProductPrices?.[index]?.removeProductFromOffer && (
                        <p className="text-xs text-right text-gray-500 mt-1">
                          Razem:{' '}
                          {(
                            watchProductPrices[index].price *
                            watchProductPrices[index].quantity
                          ).toFixed(2)}{' '}
                          zł
                        </p>
                      )}
                  </div>

                  <div>
                    <div className="flex items-center mb-2">
                      <AccessTime
                        className="text-primary mr-1"
                        fontSize="small"
                      />
                      <label className="text-xs font-medium text-gray-700">
                        Czas dostawy
                      </label>
                    </div>
                    <Controller
                      name={`productPrices.${index}.deliveryTime`}
                      control={control}
                      rules={{ required: 'Czas dostawy jest wymagany' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.productPrices?.[index]?.deliveryTime}
                          helperText={
                            errors.productPrices?.[index]?.deliveryTime
                              ?.message || ''
                          }
                          disabled={
                            watchProductPrices?.[index]?.removeProductFromOffer
                          }
                          className={
                            watchProductPrices?.[index]?.removeProductFromOffer
                              ? 'opacity-50'
                              : ''
                          }
                        >
                          {deliveryTimeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </div>

                  <div className="flex items-end sm:justify-end">
                    <Controller
                      name={`productPrices.${index}.removeProductFromOffer`}
                      control={control}
                      render={({ field }) => (
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...field}
                                checked={field.value}
                                color="error"
                              />
                            }
                            label={
                              <span className="flex items-center text-sm">
                                <Delete
                                  fontSize="small"
                                  className="text-red-500 mr-1"
                                />
                                Usuń produkt z oferty
                              </span>
                            }
                          />
                          <FormHelperText className="text-red-600">
                            {watchProductPrices?.[index]?.removeProductFromOffer
                              ? 'Produkt zostanie usunięty z oferty'
                              : ''}
                          </FormHelperText>
                        </div>
                      )}
                    />
                  </div>
                </div>
              </motion.div>
            ))}

            {(!inquiry.products || inquiry.products.length === 0) && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-gray-500">Brak produktów w zapytaniu</p>
              </div>
            )}
          </div>
        </div>

        {/* Podsumowanie i akcje */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <p className="text-lg font-medium">
                Całkowita wartość oferty:{' '}
                <span className="font-bold text-primary">
                  {(typeof totalValue === 'number' ? totalValue : 0).toFixed(2)}{' '}
                  zł
                </span>
              </p>
              <p className="text-sm text-gray-500">
                Ważna do:{' '}
                {new Date(watch('validUntil')).toLocaleDateString('pl-PL')}
              </p>
            </div>

            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center px-5 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-md shadow-sm transition-colors"
            >
              <Save className="mr-2" />
              Utwórz i wyślij ofertę
            </motion.button>
          </div>
        </div>
      </form>
    </motion.div>
  );
}

export default OfferForm;
